<template>
  <div>
    <v-form lazy-validation ref="form" v-model="formValid">
      <v-tabs class="sub-menu md-3" slider-color="primary">
        <v-tab class="sub-menu-item" key="general">
          {{ $t("settings.systemSettings.menu.general") }}
        </v-tab>
        <v-tab
          :disabled="settings.delegation_order_type === 1"
          class="sub-menu-item"
          key="delegation-order"
          v-show="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2"
        >
          {{ $t("settings.systemSettings.menu.delegationOrder") }}
        </v-tab>
        <v-tab
          class="sub-menu-item"
          key="API"
          v-show="
            $store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2 ||
              $store.getters['userProperties'].system_settings.is_module_travel_service_enabled === 2
          "
        >
          {{ $t("settings.systemSettings.menu.api") }}
        </v-tab>
        <v-tab class="sub-menu-item" key="IP">
          {{ $t("settings.systemSettings.menu.ip") }}
        </v-tab>
        <v-tab-item class="mt-2" key="general">
          <v-select :items="delegationEmailTypes" :label="$t('settings.systemSettings.emailNotificationSetting')" v-model="settings.delegation_emails" />
          <v-select
            v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2"
            :items="delegationOrderRequiredOption"
            :label="$t('settings.systemSettings.isDelegationOrderRequired')"
            v-model="settings.delegation_order_type"
          />
          <v-select
            :items="translationModifications"
            :label="$t('settings.systemSettings.translationModification')"
            v-model="settings.translations_modifications"
          />
          <fieldset class="custom-fieldset">
            <legend>{{ $t("settings.systemSettings.safeLevelSettings") }}</legend>
            <v-select :items="passwordExpire" :label="$t('settings.systemSettings.passwordStandardExpireAt')" v-model="settings.password_expiration" />
            <amount-input
              :label="$t('settings.systemSettings.blockAfterFailedTry')"
              :rules="[v => v > 0 || $t('global.valueNotAllowed')]"
              :model.sync="settings.block_after_failed_try"
            />
            <amount-input
              :label="$t('settings.systemSettings.blockAfterFailedTryTime')"
              :rules="[v => v > 0 || $t('global.valueNotAllowed')]"
              :model.sync="settings.block_after_failed_try_time"
            />
            <amount-input
              :label="$t('settings.systemSettings.sessionTime')"
              :rules="[v => v > 0 || $t('global.valueNotAllowed'), v => v >= 5 || $t('minimum 5')]"
              :model.sync="settings.session_time"
            />

            <fieldset class="custom-fieldset">
              <legend>{{ $t("settings.systemSettings.passwordRequirements") }}</legend>
              <amount-input
                :label="$t('settings.systemSettings.minimumPasswordLength')"
                :rules="[v => v > 0 || $t('global.valueNotAllowed')]"
                :model.sync="settings.minimum_password_length"
              />
              <v-select
                :items="enableDisable"
                :label="$t('settings.systemSettings.lowerLettersRequiredInPassword')"
                v-model="settings.lower_letters_required_in_password"
              />
              <v-select
                :items="enableDisable"
                :label="$t('settings.systemSettings.upperLettersRequiredInPassword')"
                v-model="settings.upper_letters_required_in_password"
              />
              <v-select :items="enableDisable" :label="$t('settings.systemSettings.digitsRequiredInPassword')" v-model="settings.digits_required_in_password" />
              <v-select
                :items="enableDisable"
                :label="$t('settings.systemSettings.specialCharactersRequiredInPassword')"
                v-model="settings.special_characters_required_in_password"
              />
            </fieldset>
          </fieldset>

          <fieldset v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2" class="custom-fieldset">
            <legend>{{ $t("settings.systemSettings.reports") }}</legend>
            <ajax-single-file
              :items="fileReportItems"
              :model.sync="headerReportImageFileId"
              :label="$t('settings.systemSettings.documentReportHeaderImage')"
              :max-file-size-k-b="128"
            />
            <v-autocomplete
              :items="reportTypeNames"
              :label="$t('settings.systemSettings.allowedReportTypes')"
              clearable
              multiple
              v-model="settings.allowed_report_types"
            />
            <v-autocomplete
              :items="reportFileFormats"
              :label="$t('settings.systemSettings.allowedReportFileTypes')"
              clearable
              multiple
              v-model="settings.allowed_report_file_formats"
            />
          </fieldset>

          <fieldset v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2" class="custom-fieldset">
            <legend>{{ $t("settings.systemSettings.documentsView") }}</legend>
            <ajax-single-file
              :items="fileItems"
              :model.sync="headerImageFileId"
              :label="$t('settings.systemSettings.documentHeaderImage')"
              :max-file-size-k-b="128"
            />
            <v-select :items="enableDisable" :label="$t('settings.systemSettings.pdfAttachmentOnly')" v-model="settings.pdf_attachment_only" />
            <v-select
              :items="delegationDocumentViewTypes"
              :label="$t('settings.systemSettings.settlementDocumentTemplate')"
              v-model="settings.delegation_settlement_document_view"
            />
            <v-select
              :items="enableDisable"
              :label="$t('settings.systemSettings.mileageForNonPrivateVehicles')"
              v-model="settings.mileage_for_non_private_vehicles"
            />
            <v-select
              :items="enableDisable"
              :label="$t('settings.systemSettings.delegationSettlementDocumentCopyDelegationOrder')"
              v-model="settings.delegation_settlement_document_copy_delegation_order"
            />
            <v-select :items="documentLanguage" :label="$t('settings.systemSettings.documentLanguage')" v-model="settings.document_language" />
          </fieldset>

          <fieldset v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2" class="custom-fieldset">
            <legend>{{ $t("settings.systemSettings.documentNumbers") }}</legend>
            <v-checkbox
              :label="$t('settings.systemSettings.synchroDelegationSettlementAndDelegationOrderNumber')"
              v-model="settings.is_synchro_numbers"
              :true-value="2"
              :false-value="1"
              v-if="settings.delegation_order_type === 3"
            />
            <v-select
              :items="delegationNumberSettingType"
              :label="$t('settings.systemSettings.delegationNumberSettingType')"
              @change="
                value => {
                  changeAfterConfirm(value, settings, 'delegation_number_setting_type');
                }
              "
              v-model="contextDelegationNumberSettingType"
            />

            <v-tooltip top>
              <template #activator="{on}">
                <v-select
                  :items="delegationNumberDate"
                  :label="$t('settings.systemSettings.delegationNumberDate')"
                  v-on="on"
                  @change="
                    value => {
                      changeAfterConfirm(value, settings, 'delegation_number_date');
                    }
                  "
                  v-model="contextDelegationNumberDate"
                />
              </template>
              {{ $t("settings.systemSettings.changeThisSettingCanBlock") }}
            </v-tooltip>

            <v-select
              :items="availableDelegationNumberTemplates"
              :label="$t('settings.systemSettings.delegationNumberTemplate')"
              :rules="settings.validateTemplateMask()"
              @change="
                value => {
                  changeAfterConfirm(value, settings, 'delegation_number_template');
                }
              "
              v-model="contextDelegationNumberTemplate"
            >
              <div slot="append-outer" style="position: relative; top: -13px;">
                <v-btn block color="accent" to="/settings/system-settings/document-number-template">
                  {{ $t("settings.systemSettings.documentNumberTemplates") }}
                </v-btn>
              </div>
            </v-select>
          </fieldset>

          <fieldset v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2" class="custom-fieldset">
            <legend>{{ $t("settings.systemSettings.delegationFields") }}</legend>
            <v-autocomplete
              :items="transportSettlementTypes"
              :label="$t('settings.systemSettings.availableTransportSettlement')"
              clearable
              item-text="text"
              item-value="value"
              multiple
              v-model="settings.allowed_transport_settlement"
            />

            <v-autocomplete
              :items="transportTypes"
              :label="$t('settings.systemSettings.availableTransportType')"
              clearable
              item-text="name"
              item-value="id"
              multiple
              v-model="settings.allowed_transport_types"
            />

            <v-select
              :items="requiredOptionalNotAvailable"
              :label="$t('settings.systemSettings.costCentreOnDelegation')"
              v-model="settings.cost_centre_on_delegations"
            />

            <v-select :items="enableDisable" :label="$t('settings.systemSettings.delegationTask')" v-model="settings.is_delegation_task_enabled" />

            <v-select
              :items="enableDisable"
              :label="$t('settings.systemSettings.reInvoicingOnExternalContractorModule')"
              v-model="settings.external_customers_module"
            />

            <v-autocomplete
              :items="accommodationSettlementTypes"
              :label="$t('settings.systemSettings.availableAccommodationType')"
              clearable
              item-text="text"
              item-value="value"
              multiple
              v-model="settings.allowed_accommodation_settlement"
            />

            <v-select
              :items="requiredOptional"
              :label="$t('settings.systemSettings.transportAttachmentRequired')"
              v-model="settings.transport_attachment_required"
            />

            <v-select
              :items="requiredOptional"
              :label="$t('settings.systemSettings.accommodationAttachmentRequired')"
              v-model="settings.accommodation_attachment_required"
            />

            <v-select
              :items="requiredOptional"
              :label="$t('settings.systemSettings.otherCostAccountNumberRequired')"
              v-model="settings.other_cost_account_number_required"
            />

            <v-select
              :items="requiredOptional"
              :label="$t('settings.systemSettings.otherTransportCommentRequired')"
              v-model="settings.other_transport_comment_required"
            />

            <v-select
              :items="requiredOptional"
              :label="$t('settings.systemSettings.transportAlreadySettledCommentRequired')"
              v-model="settings.transport_already_settled_comment_required"
            />

            <v-select
              :items="requiredOptional"
              :label="$t('settings.systemSettings.accommodationAlreadySettledCommentRequired')"
              v-model="settings.accommodation_already_settled_comment_required"
            />

            <v-select
              :items="verifyDistance"
              :label="$t('settings.systemSettings.distanceVerify')"
              v-model="settings.delegation_google_distance_verification"
            />

            <v-text-field
              :label="$t('settings.systemSettings.allowedDifferenceMapsApi') + ' [%]'"
              suffix="%"
              v-model="settings.delegation_google_distance_verification_allowed_difference"
            />

            <v-select
              :items="delegationAdvanceFlowType"
              :label="$t('settings.systemSettings.delegationAdvanceFlowType')"
              v-model="settings.delegation_advance_flow"
            />

            <v-select :items="allowDeny" :label="$t('settings.systemSettings.multipleAdvances')" v-model="settings.is_allowed_multiple_delegation_advance" />
          </fieldset>
        </v-tab-item>
        <v-tab-item class="mt-2" key="delegation-order">
          <v-select
            :items="enableDisable"
            :label="$t('settings.systemSettings.isDelegationConfirmSignRequired')"
            v-model="settings.delegation_order_confirm_is_required"
          />

          <v-select
            :items="delegationNumberSettingType"
            :label="$t('settings.systemSettings.delegationOrderNumberSettingType')"
            v-model="contextDelegationOrderNumberSettingType"
            @change="
              value => {
                changeAfterConfirm(value, settings, 'delegation_order_number_setting_type');
              }
            "
          />

          <v-tooltip top>
            <template #activator="{on}">
              <v-select
                :items="delegationNumberDate"
                :label="$t('settings.systemSettings.delegationNumberDate')"
                v-on="on"
                v-model="contextDelegationOrderNumberDate"
                @change="
                  value => {
                    changeAfterConfirm(value, settings, 'delegation_order_number_date');
                  }
                "
              />
            </template>
            {{ $t("settings.systemSettings.changeThisSettingCanBlock") }}
          </v-tooltip>

          <v-select
            :items="availableDelegationOrderNumberTemplates"
            :label="$t('settings.systemSettings.delegationNumberTemplate')"
            :rules="settings.validateDelegationOrderTemplate()"
            v-model="contextDelegationOrderNumberTemplate"
            @change="
              value => {
                changeAfterConfirm(value, settings, 'delegation_order_number_template');
              }
            "
          >
            <div slot="append-outer">
              <v-btn block color="accent" to="/settings/system-settings/document-number-template">
                {{ $t("settings.systemSettings.documentNumberTemplates") }}
              </v-btn>
            </div>
          </v-select>

          <v-divider class="my-3" />

          <h2>{{ $t("settings.systemSettings.settlementBasedOnOrder") }}</h2>
          <!--

                                        <v-select
                                                :items="allowDeny"
                                                label="Dopuszczalna różnica miejsca rozpoczęcia i zakończenia delegacji względem polecenia"
                                                v-model="settings.delegation_from_order_start_end_location_change"
                                        >
                                        </v-select>

                                        <v-select
                                                :items="allowDeny"
                                                label="Dopuszczalna różnica miejscowości docelowej względem polecenia"
                                                v-model="settings.delegation_from_order_start_end_location_change"
                                        >
                                        </v-select>
                    -->
          <v-select
            :items="allowDeny"
            :label="$t('settings.systemSettings.allowedDifferenceTransportType')"
            v-model="settings.delegation_from_order_transport_change"
          />
          <v-select
            :items="allowDeny"
            :label="$t('settings.systemSettings.allowedDifferenceCostCentre')"
            v-model="settings.delegation_from_order_cost_centre_change"
          />
          <!--
                                        <v-select
                                                :items="allowDeny"
                                                label="Dopuszczalne dodatkowe kraje delegacji"
                                                v-model="settings.delegation_from_order_start_end_location_change"
                                        >
                                        </v-select>

                                        <v-select
                                                :items="delegationFromOrderDateValidation"
                                                label="Dopuszczalna różnica dat względem polecenia delegacji"
                                                v-model="settings.delegation_from_order_start_end_location_change"
                                        >
                                        </v-select>

                                        <v-select
                                                :items="delegationFromOrderDateValidationType"
                                                label="Sposób walidacji daty"
                                                v-model="settings.delegation_from_order_start_end_location_change"
                                        >
                                        </v-select>
                    -->
        </v-tab-item>
        <v-tab-item class="mt-2" key="API">
          <v-text-field
            :label="$t('settings.systemSettings.googleMapsPlacesApiKey')"
            :placeholder="$t('settings.systemSettings.settingPredefinedKeys')"
            v-model="settings.google_maps_places_api_key"
          />
          <v-text-field
            :label="$t('settings.systemSettings.googleMapsGeocodingApiKey')"
            :placeholder="$t('settings.systemSettings.settingPredefinedKeys')"
            v-model="settings.google_maps_api_geocoding_api_key"
          />
          <v-text-field
            :label="$t('settings.systemSettings.googleMapsDistanceMatrixApiKey')"
            :placeholder="$t('settings.systemSettings.settingPredefinedKeys')"
            v-model="settings.google_maps_distance_matrix_api_key"
          />
        </v-tab-item>
        <v-tab-item class="mt-2" key="IP" eager>
          <v-select :label="$t('settings.systemSettings.accessFromNonBlockedIp')" v-model="settings.access_from_non_blocked_ip" :items="allowDeny" />
          <v-combobox ref="whitelist" label="Whitelist" multiple v-model="settings.ip_whitelist" chips />
          <v-combobox ref="blacklist" label="BlackList" multiple v-model="settings.ip_blacklist" chips />
        </v-tab-item>
      </v-tabs>
    </v-form>
    <div>
      <v-btn :disabled="!formValid || $store.getters['Ajax/loading']" @click="save()" block color="accent">{{ $t("global.save") }}</v-btn>
    </div>
  </div>
</template>

<script>
import SystemSettingsFUll from "../../../models/settings/systemSettingsFull";
import {
  delegationFromOrderDateValidation,
  delegationFromOrderDateValidationType,
  delegationNumberDate,
  delegationNumberSettingType,
  delegationOrderRequiredOption,
  verifyDistance,
  documentLanguage,
  delegationDocumentViewTypes
} from "../../../staticData/SettingsTypes";

import { allTransportTypes } from "../../../staticData/TransportTypes";
import { allTransportSettlementTypes } from "../../../staticData/TransportSettlementTypes";
import { allAccommodationSettlementTypes } from "../../../staticData/AccommodationSettlementTypes";
import delegationEmailTypes from "../../../staticData/DelegationEmailTypes";
import AjaxSingleFile from "../../../components/CustomInputComponents/AjaxSingleFile";
import { reportFileFormats, reportTypeNames } from "../../../staticData/Report";
import AmountInput from "../../../components/CustomInputComponents/AmountInput";
export default {
  name: "SettingsSystemSettings",
  components: { AmountInput, AjaxSingleFile },
  data: () => {
    return {
      test: [],
      test2: ["test1", "test2"],
      settings: new SystemSettingsFUll(),
      availableDelegationNumberTemplates: [],
      availableDelegationOrderNumberTemplates: [],
      formValid: true,
      headerImageFileId: null,
      headerReportImageFileId: null,
      fileItems: [],
      fileReportItems: []
    };
  },
  watch: {
    "settings.delegation_order_type"(val) {
      if (parseInt(val) !== 3) {
        this.settings.is_synchro_numbers = 1; // disabled
      }
    }
  },
  computed: {
    contextDelegationNumberTemplate: {
      get() {
        return this.settings.delegation_number_template;
      }
      // set(value) {}
    },
    contextDelegationNumberDate: {
      get() {
        return this.settings.delegation_number_date;
      }
      //set(value) {}
    },
    contextDelegationNumberSettingType: {
      get() {
        return this.settings.delegation_number_setting_type;
      }
      //set(value) {}
    },
    contextDelegationOrderNumberTemplate: {
      get() {
        return this.settings.delegation_order_number_template;
      }
      // set(value) {}
    },
    contextDelegationOrderNumberDate: {
      get() {
        return this.settings.delegation_order_number_date;
      }
      // set(value) {}
    },
    contextDelegationOrderNumberSettingType: {
      get() {
        return this.settings.delegation_order_number_setting_type;
      }
      //set(value) {}
    },

    delegationNumberDate: delegationNumberDate,
    delegationOrderRequiredOption: delegationOrderRequiredOption,
    delegationFromOrderDateValidationType: delegationFromOrderDateValidationType,
    delegationNumberSettingType: delegationNumberSettingType,
    verifyDistance: verifyDistance,
    delegationFromOrderDateValidation: delegationFromOrderDateValidation,
    documentLanguage: documentLanguage,
    transportTypes: allTransportTypes,
    transportSettlementTypes: allTransportSettlementTypes,
    accommodationSettlementTypes: allAccommodationSettlementTypes,
    delegationEmailTypes: delegationEmailTypes,
    delegationDocumentViewTypes,
    reportFileFormats,
    reportTypeNames,
    translationModifications() {
      return [
        {
          value: 0,
          text: this.$t("$staticData.settingsTypes.revertDefaultValue")
        },
        { value: 1, text: this.$t("global.disabled") },
        { value: "andea", text: "Andea" }
      ];
    },
    enableDisable() {
      return [
        {
          value: 0,
          text: this.$t("$staticData.settingsTypes.revertDefaultValue")
        },
        { value: 2, text: this.$t("global.enabled") },
        { value: 1, text: this.$t("global.disabled") }
      ];
    },
    allowDeny() {
      return [
        {
          value: 0,
          text: this.$t("$staticData.settingsTypes.revertDefaultValue")
        },
        { value: 1, text: this.$t("global.deny") },
        { value: 2, text: this.$t("global.allow") }
      ];
    },
    delegationAdvanceFlowType() {
      return [
        {
          value: 0,
          text: this.$t("$staticData.settingsTypes.revertDefaultValue")
        },
        { value: 1, text: this.$t("settings.systemSettings.delegationAdvanceFlowTypes.basic") },
        { value: 2, text: this.$t("settings.systemSettings.delegationAdvanceFlowTypes.extended") }
      ];
    },
    requiredOptional() {
      return [
        {
          value: 0,
          text: this.$t("$staticData.settingsTypes.revertDefaultValue")
        },
        { value: 1, text: this.$t("global.optional") },
        { value: 2, text: this.$t("global.required") }
      ];
    },
    requiredOptionalNotAvailable() {
      return [
        {
          value: 0,
          text: this.$t("$staticData.settingsTypes.revertDefaultValue")
        },
        { value: 1, text: this.$t("global.optional") },
        { value: 2, text: this.$t("global.required") },
        { value: 3, text: this.$t("global.notAvailable") }
      ];
    },
    passwordExpire() {
      return [
        { value: "never", text: this.$t("settings.systemSettings.passwordExpireNever") },
        { value: "+1 week", text: this.$t("settings.systemSettings.passwordExpireWeek") },
        { value: "+2 week", text: this.$t("settings.systemSettings.passwordExpire2Weeks") },
        { value: "+1 month", text: this.$t("settings.systemSettings.passwordExpireMonth") },
        { value: "+3 month", text: this.$t("settings.systemSettings.passwordExpire3Months") },
        { value: "+6 month", text: this.$t("settings.systemSettings.passwordExpireHalfYear") },
        { value: "+1 year", text: this.$t("settings.systemSettings.passwordExpireYear") }
      ];
    }
  },
  created() {
    this.availableDelegationNumberTemplates.push({
      value: null,
      text: this.$t("global.notSet")
    });
    this.availableDelegationOrderNumberTemplates.push({
      value: null,
      text: this.$t("global.notSet")
    });
    this.settings.load();

    this.axios.get("settings/system/available-delegation-number-templates").then(resp => {
      for (let template of resp.data) {
        this.availableDelegationNumberTemplates.push(template);
      }
    });

    this.axios.get("settings/system/available-delegation-order-number-templates").then(resp => {
      for (let template of resp.data) {
        this.availableDelegationOrderNumberTemplates.push(template);
      }
    });

    this.axios.get("settings/system/get-document-header-image").then(resp => {
      this.fileItems = resp.data.document_header_image;
      if (resp.data.document_header_image.length > 0) {
        this.headerImageFileId = resp.data.document_header_image[0].id;
      }
      this.fileReportItems = resp.data.document_header_image_report;
      if (resp.data.document_header_image_report.length > 0) {
        this.headerReportImageFileId = resp.data.document_header_image_report[0].id;
      }
    });
  },
  methods: {
    save() {
      console.log(this.$refs);
      if (this.$refs.form.validate()) {
        this.$refs.blacklist.updateSelf();
        this.$refs.whitelist.updateSelf();
        this.axios
          .post("settings/system/save-document-header-image", {
            document_header_image: this.headerImageFileId,
            document_header_image_report: this.headerReportImageFileId
          })
          .then(() => {
            this.settings
              .save()
              .then(() => {
                this.$store.dispatch("AUTH_UPDATE_SETTINGS").then(() => {
                  this.$store.dispatch("snackbarPush", {
                    text: this.$t("global.saved"),
                    position: "down"
                  });
                });
              })
              .catch(() => {
                this.$store.dispatch("alertDialog", {
                  content: this.$t("global.error.generalError.content"),
                  title: this.$t("global.error.generalError.title")
                });
              });
          });
      }
    },

    changeAfterConfirm(value, settingsModel, propertyName) {
      let confirm = this.$store.dispatch("alertDialog", {
        content: this.$t("settings.systemSettings.automaticNumberGenerationCanBeBrokenConfirm.content"),
        title: this.$t("settings.systemSettings.automaticNumberGenerationCanBeBrokenConfirm.title")
      });
      confirm.then(() => {
        settingsModel[propertyName] = value;
      });
    }
  }
};
</script>

<style scoped></style>
